import classnames from "classnames";
import React, { FC, useMemo } from "react";

import { getGiftCardPath } from "@utils/configuration/aem";
import { ConditionalWrapper } from "@utils/helpers";
import { getProductConfigurationPreview } from "@utils/product-configuration";
import { getProductConfiguration } from "@utils/productConfiguration/productConfiguration.utils";
import { formatPrice } from "@utils/productPrice";

import { CartItemModel } from "@redux/modules/api/data/cart/@types/cart.model";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { Button } from "@components/General/Button/button.component";
import { Col } from "@components/General/Grid/col.component";
import { Row } from "@components/General/Grid/row.component";
import { ShoppingCartSummaryRenditions } from "@components/Product/ProductImage/mappedRenditions";
import { ProductImageContainer } from "@components/Product/ProductImage/productImage.container";
import { ConfigurableProductImageComponent } from "@components/ShoppingCart/productCollectionBlock/configurableProductImage/configurableProductImage.component";

type Props = {
    cartItem: CartItemModel;
    activeId?: string;
    inAnimation: boolean;
    removeFromCart?: (id: string, quantity: number, free: boolean) => void;
    productLinkingAvailable: boolean;
};

export const ShoppingCartSummaryItem: FC<Props> = ({
    cartItem,
    activeId,
    inAnimation,
    removeFromCart,
    productLinkingAvailable,
}: Props) => {
    const { t } = useGranite();

    const productConfiguration = useMemo(
        () => getProductConfiguration(cartItem.sku),
        [cartItem.sku],
    );

    const removeArticle = (id: string, quantity: number, free: boolean) => {
        if (removeFromCart) {
            removeFromCart(id, quantity, free);
        }
    };

    const useProductLink = (cartItem: CartItemModel): boolean =>
        productLinkingAvailable &&
        !cartItem.warrantyDeposit &&
        !cartItem.articleConfiguration;

    const getProductUrl = (cartItem: CartItemModel): string => {
        if (cartItem.giftCardArticle) {
            return `${getGiftCardPath()}?cartid=${cartItem.id}`;
        }

        if (Object.keys(cartItem.configuration || {}).length && cartItem?.id) {
            return `${cartItem.url}?cartid=${cartItem.id}`;
        }

        return cartItem.url!;
    };

    return (
        <Row
            noGutters={true}
            className={classnames(
                "shoppingCartSummary__article small font-weight-light border-bottom",
                activeId === cartItem.id && inAnimation
                    ? "shoppingCartSummary__article--loading"
                    : "",
            )}
        >
            <Col xs={3} className="my-auto py-2">
                {!cartItem.warrantyDeposit &&
                    !cartItem.articleConfiguration && (
                        <ConditionalWrapper
                            condition={useProductLink(cartItem)}
                            wrapper={children => (
                                <a
                                    href={getProductUrl(cartItem)}
                                    title={cartItem.title}
                                >
                                    {children}
                                </a>
                            )}
                        >
                            <ProductImageContainer
                                title={cartItem.title}
                                medias={cartItem.medias}
                                isEcoChequesAccepted={false}
                                renditions={ShoppingCartSummaryRenditions}
                            />
                        </ConditionalWrapper>
                    )}
                {cartItem.articleConfiguration && (
                    <ConfigurableProductImageComponent
                        title={cartItem.title}
                        imageUrl={cartItem.articleConfiguration.imageURL}
                    />
                )}
            </Col>
            <Col xs={5} className="my-auto p-2">
                {useProductLink(cartItem) && (
                    <a href={getProductUrl(cartItem)} className="text-dark">
                        {cartItem.title}{" "}
                        {getProductConfigurationPreview(
                            cartItem.configuration,
                            productConfiguration,
                        )}
                    </a>
                )}
                {!useProductLink(cartItem) &&
                    `
                    ${cartItem.title}  ${getProductConfigurationPreview(
                        cartItem.configuration,
                        productConfiguration,
                    )}
                `}
            </Col>
            <Col xs={1} className="my-auto py-2 text-center">
                {cartItem.quantity}
            </Col>
            <Col xs={3} className="my-auto py-2 text-right font-weight-bold">
                <div className="d-flex justify-content-end align-content-center">
                    <div className="align-self-center">
                        {cartItem.showStrippedPrice && (
                            <s className="font-weight-light d-block">
                                {formatPrice(cartItem.strikethroughPrice)}
                            </s>
                        )}
                        <span className="d-block">
                            {formatPrice(cartItem.salesPrice, cartItem.free)}
                        </span>
                    </div>
                    {removeFromCart && !cartItem.warrantyDeposit && (
                        <div className="shoppingCartSummary__remove align-self-center">
                            {(!cartItem.free ||
                                cartItem.freeEntryAddedByCustomer) && (
                                <Button
                                    color={"link"}
                                    type={"button"}
                                    size={"sm"}
                                    className="p-0 pl-2"
                                    test={`shoppingCartSummary-${cartItem.sku}-button-delete`}
                                    onClick={() => {
                                        removeArticle(
                                            cartItem.id!,
                                            cartItem.quantity,
                                            cartItem.freeEntryAddedByCustomer,
                                        );
                                    }}
                                    aria-label={t(
                                        "accessibility.shoppingCartSummary.remove",
                                    )}
                                >
                                    <i className="las la-trash la-lg" />
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </Col>
        </Row>
    );
};
